import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'

// ✅ DEBOUNCE: Evitar chamadas duplicadas
const DEBOUNCE_MS = 800;

// ✅ Recebe filaId como parâmetro para evitar race conditions
export function useDiscadoraPause(filaId?: string | null) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [isPaused, setIsPaused] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentMotivo, setCurrentMotivo] = useState<string | null>(null)
  const [pausedSince, setPausedSince] = useState<Date | null>(null)
  const [ramal, setRamal] = useState<string | null>(null)
  const [localPauseActive, setLocalPauseActive] = useState(false)
  
  // Ref para evitar remontagem da subscription
  const localPauseActiveRef = useRef(false)
  // ✅ Ref para debounce do togglePause
  const lastToggleRef = useRef<number>(0)
  
  // ✅ Sincronizar ref com estado
  useEffect(() => {
    localPauseActiveRef.current = localPauseActive
  }, [localPauseActive])

  // Buscar status atual ao montar + Realtime subscription
  useEffect(() => {
    if (!user?.id) return

    // Sempre seta o ramal (mesmo null) — antes só setava se preenchido,
    // o que cacheava "vazio" pra sempre se o fetch caísse em token-refresh
    // ou RLS momentâneo. Agora setRamal sempre dispara → re-renderiza →
    // o retry abaixo dispara se ramal ficou null.
    const fetchStatus = async () => {
      const { data: usuario, error: usuarioErr } = await supabase
        .from('usuarios')
        .select('ramal')
        .eq('id', user.id)
        .maybeSingle()

      if (usuarioErr) {
        console.error('[useDiscadoraPause] Falha ao buscar ramal:', usuarioErr)
        // Não muda o ramal — mantém o que tinha. Próximo retry pega.
        return
      }

      setRamal(usuario?.ramal ?? null)

      if (usuario?.ramal) {
        const { data: status } = await supabase
          .from('discadora_status_agente')
          .select('*')
          .eq('usuario_id', user.id)
          .maybeSingle()

        if (status) {
          setIsPaused(status.status === 'pausado')
          setCurrentMotivo(status.motivo_atual_nome)
          if (status.pausado_desde) {
            setPausedSince(new Date(status.pausado_desde))
          }
        }
      }
    }

    fetchStatus()
    
    // Realtime subscription para mudanças no status
    const channel = supabase
      .channel(`discadora-status-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'discadora_status_agente',
          filter: `usuario_id=eq.${user.id}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const newStatus = payload.new as any
            
            // ✅ Debounce de 500ms para evitar re-renders excessivos
            setTimeout(() => {
              // Usar ref em vez de estado para evitar remontagem
              if (localPauseActiveRef.current && newStatus.status === 'pausado') {
                setLocalPauseActive(false)
                return
              }
              
              // Só atualizar estado se não houver pausa local pendente
              if (!localPauseActiveRef.current || newStatus.status !== 'pausado') {
                setIsPaused(newStatus.status === 'pausado')
                setCurrentMotivo(newStatus.motivo_atual_nome)
                if (newStatus.pausado_desde) {
                  setPausedSince(new Date(newStatus.pausado_desde))
                } else {
                  setPausedSince(null)
                }
              }
            }, 500)
          }
        }
      )
      .subscribe()

    // Realtime em usuarios — se admin gravar ramal novo, tela atualiza sem F5.
    // Cobre o caso da Maria Eduarda: ramal salvo no banco mas hook cacheou vazio
    // por causa de token-refresh momentâneo.
    const channelUsuarios = supabase
      .channel(`usuarios-ramal-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'usuarios',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          const novoRamal = (payload.new as any)?.ramal || null
          setRamal(novoRamal)
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
      supabase.removeChannel(channelUsuarios)
    }
  }, [user?.id])

  // Retry de ramal: se ramal ficou null mas user existe, re-busca a cada 10s
  // até pegar. Cobre a race do token-refresh sem precisar de F5.
  useEffect(() => {
    if (ramal || !user?.id) return
    const id = setTimeout(async () => {
      const { data: usuario } = await supabase
        .from('usuarios')
        .select('ramal')
        .eq('id', user.id)
        .maybeSingle()
      if (usuario?.ramal) {
        setRamal(usuario.ramal)
      }
    }, 10_000)
    return () => clearTimeout(id)
  }, [ramal, user?.id])

  const togglePause = useCallback(async (
    motivo_id: string | null, 
    observacao?: string
  ) => {
    // ✅ DEBOUNCE: Ignorar chamadas duplicadas em 800ms
    const now = Date.now();
    if (now - lastToggleRef.current < DEBOUNCE_MS) {
      console.log('⏳ [useDiscadoraPause] togglePause ignorado (debounce)');
      return;
    }
    lastToggleRef.current = now;

    if (!ramal) {
      toast.error('Ramal não configurado para este usuário')
      return
    }

    // ✅ VALIDAR FILA ATIVA (exceto para unpause)
    if (!isPaused && !filaId) {
      toast.error('❌ Selecione uma fila antes de pausar')
      return
    }

    setIsLoading(true)
    const action = isPaused ? 'unpause' : 'pause'

    try {
      // ✅ CRÍTICO: SALVAR NO BANCO IMEDIATAMENTE
      if (action === 'pause') {
        setLocalPauseActive(true)
        setIsPaused(true)
        
        const agora = new Date()
        setPausedSince(agora)
        
        // Buscar nome do motivo
        let motivoNome = null
        if (motivo_id) {
          const { data: motivo } = await supabase
            .from('discadora_motivos_pausa')
            .select('nome')
            .eq('id', motivo_id)
            .single()
          motivoNome = motivo?.nome || null
        }
        
        setCurrentMotivo(motivoNome)
        
        // SALVAR NO BANCO
        const { error: dbError } = await supabase
          .from('discadora_status_agente')
          .update({
            status: 'pausado',
            motivo_atual_id: motivo_id,
            motivo_atual_nome: motivoNome,
            pausado_desde: agora.toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('usuario_id', user!.id)
        
        if (dbError) {
          toast.error('Erro ao salvar pausa')
          setIsPaused(false)
          setPausedSince(null)
          setCurrentMotivo(null)
          setLocalPauseActive(false)
          setIsLoading(false)
          return
        }
        
        toast.success(`⏸️ Agente pausado${motivoNome ? `: ${motivoNome}` : ''}`)
      }

      // Unpause: salvar no banco DIRETAMENTE
      if (action === 'unpause') {
        const { error: dbError } = await supabase
          .from('discadora_status_agente')
          .update({
            status: 'ativo',
            motivo_atual_id: null,
            motivo_atual_nome: null,
            pausado_desde: null,
            updated_at: new Date().toISOString()
          })
          .eq('usuario_id', user!.id)
        
        if (dbError) {
          toast.error('Erro ao despausar')
          setIsLoading(false)
          return
        }
        
        setLocalPauseActive(false)
        setIsPaused(false)
        setCurrentMotivo(null)
        setPausedSince(null)
        toast.success('▶️ Agente despausado')
        
        // ✅ DISPARAR EVENTO CUSTOMIZADO PARA LIMPAR PAYLOAD
        window.dispatchEvent(new CustomEvent('discadora:clear-payload'));
        
        // CRÍTICO: Invalidar cache do atendimento
        queryClient.setQueryData(['discadora-atendimento-ativo', user!.id], null);
        queryClient.invalidateQueries({ queryKey: ['discadora-atendimento-ativo', user!.id] });
        
        // ✅ LIMPAR ATENDIMENTOS ÓRFÃOS EM BACKGROUND
        supabase
          .from('discadora_atendimentos')
          .update({
            status: 'finalizado',
            finalizado_em: new Date().toISOString(),
          })
          .eq('usuario_id', user!.id)
          .eq('status', 'ativo')
          .is('tabulacao_id', null)
          .then(() => {});
      }

      // 🚀 FIRE-AND-FORGET: Edge Function em background
      supabase.functions.invoke('discadora-pause-agent', {
        body: {
          ramal,
          fila_id: filaId,
          motivo_id,
          action,
          observacao
        }
      }).catch((err) => console.error('[useDiscadoraPause] Falha ao invocar discadora-pause-agent:', err))

    } catch (error: any) {
      toast.error('Erro inesperado. Tente novamente.')
      setIsPaused(false)
      setPausedSince(null)
      setCurrentMotivo(null)
      setLocalPauseActive(false)
    } finally {
      setIsLoading(false)
    }
  }, [ramal, isPaused, filaId, user, queryClient])

  // Flip local IMEDIATO (tabulacao Sucesso grava a pausa direto no banco; o realtime pode atrasar)
  const marcarPausadoLocal = useCallback((motivoNome?: string | null) => {
    setIsPaused(true)
    setCurrentMotivo(motivoNome ?? null)
    setPausedSince(new Date())
    setLocalPauseActive(true)
  }, [])

  return useMemo(() => ({
    isPaused,
    isLoading,
    currentMotivo,
    pausedSince,
    ramal,
    togglePause,
    marcarPausadoLocal
  }), [isPaused, isLoading, currentMotivo, pausedSince, ramal, togglePause, marcarPausadoLocal])
}
